import { useEffect, useMemo, useState } from 'react'
import moment from 'moment'

import type { FlightsClassTypes } from 'constants/flight'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import Places from 'constants/places'
import { EmptyArray, nonNullable } from 'lib/array/arrayUtils'
import getOfferListKey from 'lib/offer/offerListKey'
import { useAppSelector } from 'hooks/reduxHooks'
import useOffers from 'hooks/Offers/useOffers'
import useStreamEnded from 'hooks/Offers/useStreamEnded'
import { SEARCH_STREAMING_PAGE_SIZE } from 'search/initialiseSearchSocket'
import { buildBundleOfferFilters } from '../MaldivesDirectUtils'
import type { FlightDataByDate } from './types'

export interface UseMaldivesDirectHotelStreamSearchParams {
  checkInDate: string | undefined
  checkOutDate: string | undefined
  nights: number
  rooms: Array<App.Occupants>
  fareClassForSearch: FlightsClassTypes
  sortBy: App.OfferListSortByOptions | undefined
  flightDataByDate: Record<string, FlightDataByDate>
  isTourBundleMode: boolean
}

export interface MaldivesDirectHotelStreamSearch {
  bundleFilters: App.OfferListFilters
  listKey: string
  maldivesOfferIds: Array<string>
  maldivesOfferCount: number
  offers: Array<App.HotelOffer>
  isOffersLoading: boolean
  hasMoreStreamingOffers: boolean
  canStreamHotelOffers: boolean
  minRequiredOffers: number
  setOfferPage: (page: number) => void
  pageSize: number
}

/**
 * Hotel streaming search state for Maldives Direct: synthesised dates, bundle filters,
 * Redux offer list slice, loading flags, and scroll pagination (minRequiredOffers).
 */
export function useMaldivesDirectHotelStreamSearch(
  params: UseMaldivesDirectHotelStreamSearchParams,
): MaldivesDirectHotelStreamSearch {
  const {
    checkInDate,
    checkOutDate,
    nights,
    rooms,
    fareClassForSearch,
    sortBy,
    flightDataByDate,
    isTourBundleMode,
  } = params

  const pageSize = SEARCH_STREAMING_PAGE_SIZE
  const [offerPage, setOfferPage] = useState(1)
  const minRequiredOffers = offerPage * pageSize

  const cheapestFlightCheckIn = useMemo(() => {
    if (checkInDate) return undefined
    let cheapestDate: string | undefined
    let cheapestPrice = Infinity
    for (const [date, data] of Object.entries(flightDataByDate)) {
      if (!data.fetching && data.totalPrice !== undefined && data.totalPrice > 0 && data.totalPrice < cheapestPrice) {
        cheapestPrice = data.totalPrice
        cheapestDate = date
      }
    }
    return cheapestDate
  }, [checkInDate, flightDataByDate])

  const effectiveCheckIn = checkInDate ?? cheapestFlightCheckIn ?? ''
  const effectiveCheckOut = checkInDate
    ? (checkOutDate ?? '')
    : (cheapestFlightCheckIn ? moment(cheapestFlightCheckIn).add(nights, 'days').format(ISO_DATE_FORMAT) : '')

  const hasNoFlightDateCandidates = !checkInDate
    && !cheapestFlightCheckIn
    && Object.keys(flightDataByDate).length === 0

  // Do not stream until hotel dates are known, except when flight search completes with no date
  // candidates so we can still run a fallback hotel stream instead of getting stuck in loading.
  const canStreamHotelOffers = Boolean((effectiveCheckIn && effectiveCheckOut) || hasNoFlightDateCandidates)

  const bundleFilters = useMemo<App.OfferListFilters>(() => {
    const filters = buildBundleOfferFilters({
      destinationId: Places.Maldives.id,
      destinationName: Places.Maldives.name,
      checkIn: effectiveCheckIn,
      checkOut: effectiveCheckOut,
      rooms: rooms,
      fareClass: fareClassForSearch as App.OfferListFilters['fareClass'],
    })
    const { limit: _, ...filtersWithoutLimit } = filters
    return {
      ...filtersWithoutLimit,
      rollout: ['charterFlightAvailability'],
      sortBy: sortBy || undefined,
    }
  }, [effectiveCheckIn, effectiveCheckOut, rooms, fareClassForSearch, sortBy])

  const listKey = useMemo(() => {
    if (!canStreamHotelOffers) return ''
    return getOfferListKey(bundleFilters)
  }, [canStreamHotelOffers, bundleFilters])

  useEffect(() => {
    setOfferPage(1)
  }, [listKey])

  const maldivesOfferList = useAppSelector(state => listKey ? state.offer.offerLists[listKey] : undefined)
  const maldivesOfferIds = maldivesOfferList?.offerIds ?? EmptyArray as Array<string>
  const maldivesOfferCount = maldivesOfferList?.offerCount ?? 0
  const streamEnded = useStreamEnded(bundleFilters, { disabled: !canStreamHotelOffers })
  const [offersMaybe] = useOffers<App.HotelOffer>(maldivesOfferIds)
  const offers = useMemo<Array<App.HotelOffer>>(() => nonNullable(offersMaybe), [offersMaybe])

  const isWaitingForCheapestDateOrFlightData = useMemo(() => {
    if (checkInDate && !checkOutDate) return true
    if (checkInDate) return false
    if (Object.values(flightDataByDate).some(d => d.fetching)) return true
    return false
  }, [checkInDate, checkOutDate, flightDataByDate])

  const isOffersLoading = useMemo(() => {
    if (!canStreamHotelOffers) {
      return isWaitingForCheapestDateOrFlightData
    }
    return !streamEnded && maldivesOfferIds.length === 0
  }, [canStreamHotelOffers, isWaitingForCheapestDateOrFlightData, streamEnded, maldivesOfferIds.length])

  const hasMoreStreamingOffers = useMemo(
    () => !isTourBundleMode && canStreamHotelOffers && (!streamEnded || maldivesOfferIds.length > minRequiredOffers),
    [isTourBundleMode, canStreamHotelOffers, streamEnded, maldivesOfferIds.length, minRequiredOffers],
  )

  return {
    bundleFilters,
    listKey,
    maldivesOfferIds,
    maldivesOfferCount,
    offers,
    isOffersLoading,
    hasMoreStreamingOffers,
    canStreamHotelOffers,
    minRequiredOffers,
    setOfferPage,
    pageSize,
  }
}
